<script setup lang="ts">
import showdown from "showdown";
import InlineFileDownload from "./InlineFileDownload.vue";

const props = defineProps<{
  text: string;
}>();

const parsedContent = computed(() => {
  if (!props.text) return "";

  const domain = useRequestURL().host.replace("www.", "");
  const regexPattern = `<a\\s+href="((?:(?:https?:\\/\\/(?:www\\.)?${domain})?\\/file\\/(\\d+))")((?:\\s+[^>]*)?)>(.*?)<\\/a>`;
  const regex = new RegExp(regexPattern, "gi");

  const withPlaceholders = props.text?.replace(regex, '<a href="$1" data-file-id="$2"$3>$4</a>');

  const videoRegex = /\[video\]\((https?:\/\/[^\s]+)\)/gi;
  const withVideoPlaceholders = withPlaceholders.replace(videoRegex, '<a href="$1" data-video-src="$1">$1</a>');

  const converter = new showdown.Converter();
  return converter.makeHtml(withVideoPlaceholders);
});

const container = ref<HTMLDivElement>();

type ProcessedBaseLink = {
  id: number;
  element: HTMLAnchorElement;
  type: "download" | "video";
};

type ProcessedDownloadLink = ProcessedBaseLink & {
  type: "download";
  fileId: number;
  text: string;
};

type ProcessedVideoLink = ProcessedBaseLink & {
  type: "video";
  videoSrc: string;
};

type ProcessedLink = ProcessedDownloadLink | ProcessedVideoLink;

const processedLinks = ref<ProcessedLink[]>([]);

onMounted(() => {
  // Cerca il segnaposto solo all'interno del componente tramite ref
  if (container.value) {
    const links = container.value.getElementsByTagName("a");

    Array.from(links).forEach((link, index) => {
      replaceDownloadLink(link, index);
      replaceVideoLink(link, index);
    });
  }
});

function replaceDownloadLink(link: HTMLAnchorElement, index: number) {
  if (!link.hasAttribute("data-file-id")) return;

  const fileId = link.getAttribute("data-file-id");
  const text = link.innerHTML;

  // Crea il componente dinamicamente e montalo nel segnaposto
  if (!fileId) return;

  processedLinks.value.push({
    id: index,
    fileId: parseInt(fileId),
    text,
    element: link,
    type: "download",
  });

  const componentContainer = document.createElement("div");
  componentContainer.setAttribute("data-replace-id", index.toString());
  componentContainer.classList.add("inline-block");

  // Sostituisci il segnaposto con il componente montato
  link.replaceWith(componentContainer);
}

function replaceVideoLink(link: HTMLAnchorElement, index: number) {
  if (!link.hasAttribute("data-video-src")) return;

  const videoSrc = link.getAttribute("data-video-src");

  processedLinks.value.push({
    id: index,
    videoSrc: videoSrc,
    element: link,
    type: "video",
  });

  const componentContainer = document.createElement("div");
  componentContainer.setAttribute("data-replace-id", index.toString());

  // Sostituisci il segnaposto con il componente montato
  link.replaceWith(componentContainer);
}
</script>

<template>
  <div
    class="mb-8 print:mb-2 prose max-w-full break-words print:prose-p:break-inside-avoid print:prose-headings:break-after-avoid prose-a:text-primary prose-td:border prose-th:border prose-td:px-5 prose-th:px-5"
    ref="container"
  >
    <div v-html="parsedContent"></div>

    <template v-for="link in processedLinks" :key="link.id">
      <Teleport :to="`[data-replace-id='${link.id}']`">
        <InlineFileDownload v-if="link.type === 'download'" :fileId="link.fileId" :text="link.text" />
        <ClientOnly v-else-if="link.type === 'video'">
          <VideoPlayer
            class="border-2 border-neutral-300"
            :options="{
              controls: true,
              sources: [{ src: link.videoSrc, type: '' }],
              aspectRatio: '16:9',
            }"
          ></VideoPlayer>
        </ClientOnly>
      </Teleport>
    </template>
  </div>
</template>
